import { useState, type MutableRefObject, type ReactNode } from 'react';
import {
  EMPTY_QUICK_PREFS,
  QUICK_SIDE_CARDS,
  cardsHiddenFor,
  type NotePhrasesView,
  type QuickSideCard,
} from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { helpersSaveShape } from '../lib/skillsMedNote.js';
import { Modal } from './Modal.js';

/**
 * ehr#732 (MEDICATION_MANAGEMENT_NOTE_10-30-V5 V4, Jay 21 A): Note settings > Helpers on a note
 * with no Quick add card. Only the Show switches of the side cards that note has (Show Last
 * session, Show Generate Notes), On by default. A switch waits for Save: Save for this client or
 * Save for all clients saves and closes, Cancel closes and drops what was not saved. The Quick add
 * lists and the other cards' switches stay as they are (lib/skillsMedNote helpersSaveShape).
 */
export function HelpersWindow({
  view,
  contactId,
  offered,
  title,
  onClose,
  onSaved,
  tabs,
  leaveRef,
}: {
  view: NotePhrasesView;
  contactId: string;
  /** The side cards this note has (their Show switches). */
  offered: readonly QuickSideCard[];
  /** The tab's name ("Helpers"), also the window title. */
  title: string;
  onClose: () => void;
  onSaved: (v: NotePhrasesView) => void;
  /** Note settings' tab strip, shown above the switches. */
  tabs?: ReactNode;
  /** Note settings asks here before leaving the tab (changes not saved ask first). */
  leaveRef?: MutableRefObject<((go: () => void) => void) | null>;
}) {
  const base = view.quick ?? EMPTY_QUICK_PREFS;
  const savedOff = helpersOff(cardsHiddenFor(base, contactId), offered);
  const [off, setOff] = useState<QuickSideCard[]>(savedOff);
  const [ask, setAsk] = useState<(() => void) | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const dirty = [...off].sort().join() !== [...savedOff].sort().join();
  const leave = (go: () => void) => {
    if (busy) return;
    if (dirty) setAsk(() => go);
    else go();
  };
  if (leaveRef) leaveRef.current = leave;
  const save = (to: 'client' | 'all') => {
    setAsk(null);
    setBusy(true);
    setErr('');
    api
      .put<NotePhrasesView>(
        '/note-phrases/quick-add',
        helpersSaveShape(base, { to, contactId, offered, off }),
      )
      .then((v) => onSaved(v))
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : `Could not save the ${title.toLowerCase()}.`),
      )
      .finally(() => setBusy(false));
  };
  return (
    <Modal
      title={title}
      onClose={() => leave(onClose)}
      className="pn-qs-modal pn-ns-modal"
      actions={
        <div className="pn-qs-foot">
          {/* V4: Cancel closes and drops the switches not saved */}
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn" onClick={() => save('all')} disabled={busy}>
            Save for all clients
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => save('client')}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save for this client'}
          </button>
        </div>
      }
    >
      {tabs}
      <div className="pn-qa-sws" data-test="helpers-sws">
        {helperRows(offered).map(([k, label]) => (
          <div className="pn-qa-sw" data-test="qa-sw-card" key={k}>
            <b>{label}</b>
            <div className="pn-seg pn-ns-seg" role="group" aria-label={label}>
              {(
                [
                  [true, 'On'],
                  [false, 'Off'],
                ] as const
              ).map(([v, t]) => (
                <button
                  key={t}
                  type="button"
                  aria-pressed={!off.includes(k) === v}
                  disabled={busy}
                  onClick={() =>
                    setOff((l) => (v ? l.filter((x) => x !== k) : [...new Set([...l, k])]))
                  }
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {err && (
        <p className="pn-inline-err" role="alert">
          {err}
        </p>
      )}
      {ask && (
        <Modal
          title="Save your changes?"
          onClose={() => setAsk(null)}
          className="pn-qs-ask"
          actions={
            <>
              <button type="button" className="btn" onClick={() => setAsk(null)}>
                Go back
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  const go = ask;
                  setAsk(null);
                  go();
                }}
              >
                Don&rsquo;t save
              </button>
              <button type="button" className="btn" onClick={() => save('all')}>
                Save for all clients
              </button>
              <button type="button" className="btn primary" onClick={() => save('client')}>
                Save for this client
              </button>
            </>
          }
        >
          <p className="pn-modal-p">You changed {title} settings and haven&rsquo;t saved them.</p>
        </Modal>
      )}
    </Modal>
  );
}

/** The Show rows, in the rail's order: "Show Last session", "Show Generate Notes". */
export function helperRows(offered: readonly QuickSideCard[]): [QuickSideCard, string][] {
  return QUICK_SIDE_CARDS.filter(([k]) => offered.includes(k)).map(([k, t]) => [k, `Show ${t}`]);
}

/** The offered cards switched Off now. */
export function helpersOff(
  hidden: readonly QuickSideCard[],
  offered: readonly QuickSideCard[],
): QuickSideCard[] {
  return hidden.filter((k) => offered.includes(k));
}
